import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import fetchEndpoint from '../../../services/fetchEndpoint';
import buildGraphEndpoint from '../../../utils/buildGraphEndpoint';
import { searchContext, userAppContext } from '../../../context/Context';
import { type AnalyticsTabKey, getAnalyticsConfig } from './analyticsConfig';
import type { AssociationRow } from './analyticsTypes';

const PAGE_SIZE = 250;

export function useAssociationAnalyticsData(
  source: string,
  view: string,
  associationTab: AnalyticsTabKey,
  searchItemsOverride?: Array<{ id: string }>,
) {
  const [data, setData] = useState<AssociationRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const { authorization, setIsAuthenticated, restrictGridResultCount } = userAppContext() || {};
  const { tab: activeTab, searchItems: contextSearchItems } = searchContext() || { tab: 'GDA', searchItems: [] };
  const config = useMemo(() => getAnalyticsConfig(associationTab, view), [associationTab, view]);
  const orderBy = config?.orderBy ?? '';
  const effectiveSearchItems = searchItemsOverride?.length ? searchItemsOverride : contextSearchItems;
  const searchItemsKey = effectiveSearchItems?.map(item => String(item?.id ?? '')).join(',') ?? '';
  const isPanelActive = activeTab === associationTab;
  const resultLimit = typeof restrictGridResultCount === 'number' ? restrictGridResultCount : null;
  const pageSize = resultLimit ?? PAGE_SIZE;

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!config || !searchItemsKey || !isPanelActive) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const firstUrl = buildGraphEndpoint(
        view,
        source,
        associationTab,
        effectiveSearchItems,
        0,
        pageSize,
        orderBy,
        'desc',
      );
      const firstResponse = await fetchEndpoint(firstUrl, authorization, setIsAuthenticated);

      if (requestId !== requestIdRef.current) return;

      if (!firstResponse) {
        setData([]);
        setIsEmpty(true);
        return;
      }

      const firstData: AssociationRow[] = firstResponse.payload || [];
      const totalElements = firstResponse?.paging?.totalElements ?? firstData.length;

      if (!firstData.length) {
        setData([]);
        setIsEmpty(true);
        return;
      }

      let completeData = [...firstData];

      if (!resultLimit) {
        const totalPages = Math.ceil(totalElements / pageSize);

        for (let page = 1; page < totalPages; page += 1) {
          const pageUrl = buildGraphEndpoint(
            view,
            source,
            associationTab,
            effectiveSearchItems,
            page,
            pageSize,
            orderBy,
            'desc',
          );
          const pageResponse = await fetchEndpoint(pageUrl, authorization, setIsAuthenticated);

          if (requestId !== requestIdRef.current) return;

          if (pageResponse?.payload?.length) {
            completeData = [...completeData, ...pageResponse.payload];
          }
        }
      } else {
        completeData = completeData.slice(0, resultLimit);
      }

      if (requestId !== requestIdRef.current) return;

      setData(completeData);
      setIsEmpty(false);
    } catch (fetchError) {
      if (requestId !== requestIdRef.current) return;
      setError(fetchError instanceof Error ? fetchError.message : 'Error al cargar los datos');
      setData([]);
      setIsEmpty(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    associationTab,
    authorization,
    isPanelActive,
    orderBy,
    pageSize,
    resultLimit,
    searchItemsKey,
    setIsAuthenticated,
    source,
    view,
  ]);

  useEffect(() => {
    if (!isPanelActive) {
      requestIdRef.current += 1;
      setData([]);
      setIsEmpty(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    setData([]);
    setIsEmpty(false);
    setIsLoading(true);
    fetchData();
  }, [fetchData, isPanelActive]);

  return { data, isLoading, isEmpty, error, config, isPanelActive };
}

export { useAssociationAnalyticsData as useGdaAnalyticsData };
